// App.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import "./index.css";

/* ========= Firebase Storage (client) =========
   1) npm i firebase
   2) Replace the firebaseConfig values below with your Firebase Web App config
      (Firebase Console -> Project settings -> General -> Your apps -> Web app)
*/
import { initializeApp } from "firebase/app";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const modernFont = { fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' };

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const fbApp = initializeApp(firebaseConfig);
const storage = getStorage(fbApp);

async function uploadImage(file, folder) {
  const safe = (file?.name || "image").replace(/\s+/g, "-");
  const objectPath = `${folder}/${Date.now()}-${safe}`;
  const fileRef = ref(storage, objectPath);
  await uploadBytes(fileRef, file, { contentType: file.type });
  return await getDownloadURL(fileRef);
}

async function deleteOldImage(oldUrl) {
  if (!oldUrl) return;
  try {
    const url = new URL(oldUrl);
    const path = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
    const fileRef = ref(storage, path);
    await deleteObject(fileRef);
  } catch (e) {
    console.error('Failed to delete old image:', e);
  }
}

const uid = () => Math.random().toString(36).slice(2, 9);

// UPDATED: use deployed API for dev (Firebase Functions)
const API = process.env.REACT_APP_API_URL;

async function getConfig() {
  const r = await fetch(`${API}/api/config`);
  if (!r.ok) throw new Error("Failed to load config");
  return r.json();
}

async function saveConfig(payload) {
  const r = await fetch(`${API}/api/config`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error("Failed to save config");
  return r.json();
}

const EXAMPLE_BG =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(`
  <svg xmlns='http://www.w3.org/2000/svg' width='1800' height='1000'>
    <defs>
      <linearGradient id='g1' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0' stop-color='#0b3a2e'/>
        <stop offset='0.55' stop-color='#0a2a3d'/>
        <stop offset='1' stop-color='#1b0f2a'/>
      </linearGradient>
      <radialGradient id='g2' cx='30%' cy='35%' r='65%'>
        <stop offset='0' stop-color='rgba(255,255,255,0.18)'/>
        <stop offset='1' stop-color='rgba(255,255,255,0)'/>
      </radialGradient>
      <radialGradient id='g3' cx='78%' cy='72%' r='70%'>
        <stop offset='0' stop-color='rgba(255,255,255,0.12)'/>
        <stop offset='1' stop-color='rgba(255,255,255,0)'/>
      </radialGradient>
    </defs>
    <rect width='1800' height='1000' fill='url(#g1)'/>
    <rect width='1800' height='1000' fill='url(#g2)'/>
    <rect width='1800' height='1000' fill='url(#g3)'/>
    <g opacity='0.14'>
      <circle cx='300' cy='760' r='260' fill='white'/>
      <circle cx='1480' cy='220' r='300' fill='white'/>
    </g>
  </svg>
`);

const EXAMPLE_ICON_A =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(`
  <svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'>
    <defs>
      <linearGradient id='ga' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0' stop-color='#f472b6'/>
        <stop offset='0.6' stop-color='#fb923c'/>
        <stop offset='1' stop-color='#a855f7'/>
      </linearGradient>
    </defs>
    <rect width='240' height='240' rx='56' fill='url(#ga)'/>
    <circle cx='120' cy='120' r='70' fill='rgba(255,255,255,0.22)'/>
  </svg>
`);

const EXAMPLE_ICON_B =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(`
  <svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'>
    <defs>
      <linearGradient id='gb' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0' stop-color='#22c55e'/>
        <stop offset='0.55' stop-color='#84cc16'/>
        <stop offset='1' stop-color='#06b6d4'/>
      </linearGradient>
    </defs>
    <rect width='240' height='240' rx='56' fill='url(#gb)'/>
    <path d='M65 140c22-35 42-50 56-53 18-4 36 10 56 45' fill='none' stroke='rgba(255,255,255,0.35)' stroke-width='22' stroke-linecap='round'/>
  </svg>
`);

const EXAMPLE_LOGO =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(`
  <svg xmlns='http://www.w3.org/2000/svg' width='900' height='280'>
    <defs>
      <filter id='s' x='-25%' y='-25%' width='150%' height='150%'>
        <feDropShadow dx='0' dy='12' stdDeviation='10' flood-color='rgba(0,0,0,0.45)'/>
      </filter>
    </defs>
    <g filter='url(#s)'>
      <text x='40' y='175' font-family='Montserrat, Arial' font-weight='900' font-size='120' fill='white' stroke='rgba(0,0,0,0.65)' stroke-width='18' paint-order='stroke fill'>WeSmoke</text>
      <text x='610' y='242' font-family='Montserrat, Arial' font-weight='900' font-size='88' fill='white' stroke='rgba(0,0,0,0.65)' stroke-width='18' paint-order='stroke fill'>Cafe</text>
    </g>
  </svg>
`);

function Modal({ open, title, onClose, children, footer, modalPos, setModalPos }) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isDragged, setIsDragged] = useState(false);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setIsDragged(true);
    setDragStart({ x: e.clientX - modalPos.x, y: e.clientY - modalPos.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setModalPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, modalPos, setModalPos, dragStart]);

  if (!open) return null;

  return (
    <div className="modalRoot">
      <div className="modalBackdrop" onClick={onClose} />
      <div
        className="modalPanel"
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'fixed', width: window.innerWidth < 768 ? '95vw' : '700px', maxWidth: window.innerWidth < 768 ? '95vw' : '700px', ...(isDragged ? { left: modalPos.x, top: modalPos.y } : { left: 0, top: 0 }) }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modalHeader" onMouseDown={handleMouseDown} style={{ cursor: 'move' }}>
          <div className="modalHeaderLeft">
            <div className="modalTitle" title={title}>
              {title}
            </div>
          </div>
          <button onClick={onClose} className="modalCloseBtn" type="button">
            Close
          </button>
        </div>

        <div className="modalBody">{children}</div>

        {footer ? <div className="modalFooter">{footer}</div> : null}
      </div>
    </div>
  );
}

function FrostTable({ table, borderRadius }) {
  return (
    <div className="menuCard" style={{ borderRadius }}>
      <table className="menuTable">
        <thead>
          <tr className="menuTheadRow">
            <th className="menuCat" style={modernFont}>{table.title}</th>
            {table.columns
              .filter((c) => c.key !== "name")
              .map((c) => (
                <th
                  key={c.key}
                  className="menuWeight"
                  style={{ ...modernFont, textShadow: "0 1px 0 rgba(0,0,0,0.35)", color: 'darkgreen', fontWeight: 'bold', fontSize: 'calc(16px * var(--fontScale))' }}
                >
                  {c.label}
                </th>
              ))}
          </tr>
        </thead>

        <tbody>
          {table.rows.map((r) => (
            <tr key={r.id} className="menuTbodyRow">
              <td className="menuCell menuCellLeft" style={{...modernFont, fontSize:'calc(16px * var(--fontScale))'}}>
                <span style={{ textShadow: "0 1px 0 rgba(255,255,255,0.35)" }}>
                  {r.name ?? ""}
                </span>
              </td>
              {table.columns
                .filter((c) => c.key !== "name")
                .map((c) => (
                  <td key={c.key} className="menuCell menuCellCenter" style={{...modernFont, fontSize:'calc(16px * var(--fontScale))'}}>
                    <span style={{ textShadow: "0 1px 0 rgba(255,255,255,0.35)" }}>
                      {r[c.key] ?? ""}
                    </span>
                  </td>
                ))}
            </tr>
          ))}

          {table.rows.length === 0 ? (
            <tr className="menuTbodyRow">
              <td colSpan={table.columns.length} className="menuEmptyRow" />
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function UploadControl({ label, value, onChange }) {
  return (
    <div className="uploadCard">
      <div className="uploadTop">
        <div className="uploadLabel">{label}</div>
        <label className="uploadBtn">
          Upload
          <input
            type="file"
            className="srOnly"
            accept="image/*"
            onChange={(e) => onChange(e.target.files?.[0] || null)}
          />
        </label>
      </div>

      <div className="uploadPreview">
        {value ? (
          <img src={value} alt="preview" className="uploadImg" />
        ) : (
          <div className="uploadEmpty" />
        )}
      </div>
    </div>
  );
}

function TextInput({ value, onChange, placeholder, className }) {
  return (
    <input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`textInput ${className || ""}`}
      maxLength={140}
    />
  );
}

function MenuPage() {
  const [adminTab, setAdminTab] = useState("menu");

  const gridRef = useRef(null);

  /* Scale UI for any screen size (including very large TVs) */
  useEffect(() => {
    const applyScale = () => {
      const w = window.innerWidth || 1920;
      const h = window.innerHeight || 1080;

      let s = Math.min(w / 1920, h / 1080);
      s = Math.max(0.85, Math.min(3.5, s));

      document.documentElement.style.setProperty("--uiScale", String(s));
    };

    applyScale();
    window.addEventListener("resize", applyScale);
    return () => window.removeEventListener("resize", applyScale);
  }, []);

  const initialTables = useMemo(
    () => [
      {
        id: "t1",
        side: "left",
        title: "Cali Packs",
        columns: [
          { key: "name", label: "" },
          { key: "c1", label: "1G" },
          { key: "c2", label: "3.5G" },
          { key: "c3", label: "7G" },
        ],
        rows: [
          { id: uid(), name: "Blue Citrus Hemp", c1: "900฿", c2: "2400฿", c3: "" },
          { id: uid(), name: "Morning Pine CBD", c1: "900฿", c2: "2400฿", c3: "" },
          { id: uid(), name: "Golden Calm Reserve", c1: "", c2: "", c3: "6200฿" },
        ],
      },
      {
        id: "t2",
        side: "left",
        title: "Cali Loose",
        columns: [
          { key: "name", label: "" },
          { key: "c1", label: "1G" },
          { key: "c2", label: "3.5G" },
          { key: "c3", label: "7G" },
          { key: "c4", label: "14G" },
          { key: "c5", label: "28G" },
        ],
        rows: [
          { id: uid(), name: "Island Lime", c1: "650฿", c2: "1900฿", c3: "3300฿", c4: "6100฿", c5: "11500฿" },
          { id: uid(), name: "Vanilla Breeze", c1: "650฿", c2: "1900฿", c3: "3300฿", c4: "6100฿", c5: "11500฿" },
          { id: uid(), name: "Purple Lotus", c1: "650฿", c2: "1900฿", c3: "3300฿", c4: "6100฿", c5: "11500฿" },
          { id: uid(), name: "Sunset Mango", c1: "650฿", c2: "1900฿", c3: "3300฿", c4: "6100฿", c5: "11500฿" },
        ],
      },
      {
        id: "t3",
        side: "left",
        title: "Designer Thai",
        columns: [
          { key: "name", label: "" },
          { key: "c1", label: "1G" },
          { key: "c2", label: "3.5G" },
          { key: "c3", label: "14G" },
          { key: "c4", label: "28G" },
        ],
        rows: [
          { id: uid(), name: "Thai Emerald", c1: "380฿", c2: "1100฿", c3: "3800฿", c4: "6800฿" },
          { id: uid(), name: "Bangkok Bloom", c1: "380฿", c2: "1100฿", c3: "3800฿", c4: "6800฿" },
          { id: uid(), name: "Chiang Mai Cloud", c1: "380฿", c2: "1100฿", c3: "3800฿", c4: "6800฿" },
        ],
      },
      {
        id: "t4",
        side: "left",
        title: "Top Thai",
        columns: [
          { key: "name", label: "" },
          { key: "c1", label: "1G" },
          { key: "c2", label: "5G" },
          { key: "c3", label: "10G" },
          { key: "c4", label: "30G" },
        ],
        rows: [{ id: uid(), name: "Top Thai Select", c1: "240฿", c2: "950฿", c3: "1750฿", c4: "2950฿" }],
      },
      {
        id: "t5",
        side: "right",
        title: "Fresh Frozen",
        columns: [
          { key: "name", label: "" },
          { key: "c1", label: "1G" },
          { key: "c2", label: "5G" },
        ],
        rows: [
          { id: uid(), name: "Hemp Rosin Clear", c1: "580฿", c2: "2400฿" },
          { id: uid(), name: "Crystal CBD Resin", c1: "580฿", c2: "2400฿" },
        ],
      },
      {
        id: "t6",
        side: "right",
        title: "Cali",
        columns: [
          { key: "name", label: "" },
          { key: "c1", label: "1G" },
          { key: "c2", label: "5G" },
        ],
        rows: [{ id: uid(), name: "Premium CBD Extract", c1: "1450฿", c2: "5800฿" }],
      },
      {
        id: "t7",
        side: "right",
        title: "Pre Rolls",
        columns: [
          { key: "name", label: "" },
          { key: "c1", label: "Single" },
          { key: "c2", label: "Bundle" },
        ],
        rows: [
          { id: uid(), name: "CBD Citrus Roll", c1: "280฿", c2: "950฿ x4" },
          { id: uid(), name: "Thai Calm Roll", c1: "140฿", c2: "480฿ x5" },
        ],
      },
    ],
    []
  );

  const [tables, setTables] = useState(initialTables);
  const [adminOpen, setAdminOpen] = useState(false);
  const [activeId, setActiveId] = useState(initialTables[0]?.id ?? "t1");
  const [draftTable, setDraftTable] = useState(null);
  const [dirty, setDirty] = useState(false);

  /* Optional: bump defaults so 30-per-side is normal */
  const [leftCap, setLeftCap] = useState(30);
  const [rightCap, setRightCap] = useState(30);
  const [capsUnlocked, setCapsUnlocked] = useState(false);

  const [bgImage, setBgImage] = useState(EXAMPLE_BG);
  const [logoImage, setLogoImage] = useState(EXAMPLE_LOGO);

  // NEW: user-controlled menu scaling (Design tab)
  const [menuUserScale, setMenuUserScale] = useState(1);

  // User-controlled font scaling (Design tab)
  const [fontScale, setFontScale] = useState(1);

  const [tableBorderRadius, setTableBorderRadius] = useState('8px');

  useEffect(() => {
    document.documentElement.style.setProperty("--menuUserScale", String(menuUserScale));
  }, [menuUserScale]);

  useEffect(() => {
    document.documentElement.style.setProperty("--fontScale", String(fontScale));
  }, [fontScale]);

  // Decor images (replaces iconA/iconB and iconAPos/iconBPos)
  const DEFAULT_DECOR_COUNT = 2;

  function makeDecor(i) {
    const isRight = i % 2 === 1;
    return {
      id: uid(),
      label: `Image ${String.fromCharCode(65 + i)}`, // A, B, C...
      src: i === 0 ? EXAMPLE_ICON_A : i === 1 ? EXAMPLE_ICON_B : null,
      anchor: isRight ? "right" : "left", // "left" | "right"
      x: -24, // offset used as left/right depending on anchor
      top: 12,
      size: 86,
      zIndex: 0,
      rotation: 0,
    };
  }

  const [decorCount, setDecorCount] = useState(DEFAULT_DECOR_COUNT);
  const [decorImages, setDecorImages] = useState(() =>
    Array.from({ length: DEFAULT_DECOR_COUNT }, (_, i) => makeDecor(i))
  );

  const [logoPos, setLogoPos] = useState({ leftPct: 50, top: -12, size: 70 });
  const [gridPos, setGridPos] = useState({ left: 50, top: 55 });

  const [warning, setWarning] = useState(null);

  const [modalPos, setModalPos] = useState({ x: 0, y: 0 });

  const removeDecor = async (idx) => {
    const d = decorImages[idx];
    if (d.src) await deleteOldImage(d.src);
    const newDecor = decorImages.filter((_, i) => i !== idx).map((x, i) => ({ ...x, label: `Image ${String.fromCharCode(65 + i)}` }));
    setDecorImages(newDecor);
    setDecorCount(newDecor.length);
    setDirty(true);
    await saveConfig({
      tables,
      leftCap,
      rightCap,
      logoPos,
      gridPos,
      decorImages: newDecor,
      menuUserScale,
      fontScale,
      bgImage,
      logoImage,
      tableBorderRadius,
    });
  };

  // UPDATED: load saved config on startup (now includes bgImage/logoImage)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const cfg = await getConfig();
        if (cancelled) return;

        if (cfg.tables) setTables(cfg.tables);

        if (cfg.leftCap != null) setLeftCap(cfg.leftCap);
        if (cfg.rightCap != null) setRightCap(cfg.rightCap);

        if (cfg.logoPos) setLogoPos(cfg.logoPos);
        if (cfg.gridPos) setGridPos(cfg.gridPos);

        if (cfg.menuUserScale != null) setMenuUserScale(cfg.menuUserScale);

        if (cfg.fontScale != null) setFontScale(cfg.fontScale);

        if (cfg.tableBorderRadius != null) setTableBorderRadius(cfg.tableBorderRadius);

        if (cfg.bgImage !== undefined) setBgImage(cfg.bgImage);
        if (cfg.logoImage !== undefined) setLogoImage(cfg.logoImage);

        // Load decor images (src are now real URLs from Storage, so cross-device works)
        if (Array.isArray(cfg.decorImages)) {
          setDecorImages(cfg.decorImages);
          setDecorCount(cfg.decorImages.length);
        } else if (cfg.iconAPos || cfg.iconBPos) {
          // Backward compatibility: if backend still has iconA/iconB positions saved
          const fallback = [makeDecor(0), makeDecor(1)];
          if (cfg.iconAPos) {
            fallback[0] = {
              ...fallback[0],
              anchor: "left",
              x: cfg.iconAPos.left ?? fallback[0].x,
              top: cfg.iconAPos.top ?? fallback[0].top,
              size: cfg.iconAPos.size ?? fallback[0].size,
            };
          }
          if (cfg.iconBPos) {
            fallback[1] = {
              ...fallback[1],
              anchor: "right",
              x: cfg.iconBPos.right ?? fallback[1].x,
              top: cfg.iconBPos.top ?? fallback[1].top,
              size: cfg.iconBPos.size ?? fallback[1].size,
            };
          }
          setDecorImages(fallback);
          setDecorCount(fallback.length);
        }
      } catch (e) {
        console.warn(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  function sideMax(side) {
    return side === "left" ? leftCap : rightCap;
  }

  function otherCountForSide(side, excludeId) {
    return tables
      .filter((t) => t.side === side && t.id !== excludeId)
      .reduce((a, t) => a + t.rows.length, 0);
  }

  const leftUsed = useMemo(
    () => tables.filter((t) => t.side === "left").reduce((a, t) => a + t.rows.length, 0),
    [tables]
  );

  const rightUsed = useMemo(
    () => tables.filter((t) => t.side === "right").reduce((a, t) => a + t.rows.length, 0),
    [tables]
  );

  /* UPDATED: content auto-fit (scales down menu grid if it would overflow)
     - Now correctly accounts for BOTH menuUserScale and current contentScale
  */
  useEffect(() => {
    const root = document.documentElement;

    const applyContentScale = () => {
      const el = gridRef.current;
      if (!el) return;

      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();

        const pad = 16;
        const vw = window.innerWidth || 1920;
        const vh = window.innerHeight || 1080;

        const csRaw = getComputedStyle(root).getPropertyValue("--contentScale").trim();
        const usRaw = getComputedStyle(root).getPropertyValue("--menuUserScale").trim();

        const currentContentScale = Number.parseFloat(csRaw) || 1;
        const currentUserScale = Number.parseFloat(usRaw) || 1;

        // rect is already transformed by userScale * contentScale, so normalize back to "base"
        const baseW = rect.width / Math.max(0.0001, currentContentScale * currentUserScale);
        const baseH = rect.height / Math.max(0.0001, currentContentScale * currentUserScale);

        const sx = (vw - pad * 2) / Math.max(1, baseW);
        const sy = (vh - pad * 2) / Math.max(1, baseH);

        let s = Math.min(1, sx, sy);
        s = Math.max(0.6, s);

        root.style.setProperty("--contentScale", String(s));
      });
    };

    applyContentScale();

    const onResize = () => applyContentScale();
    window.addEventListener("resize", onResize);

    let ro;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(() => applyContentScale());
      if (gridRef.current) ro.observe(gridRef.current);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, [tables, gridPos, logoPos, decorImages, bgImage, logoImage, menuUserScale]);

  function openAdmin() {
    const code = window.prompt("Enter 6-digit code to edit:");
    if (code !== (process.env.REACT_APP_EDIT_MENU_PASSWORD)) {
      alert("Incorrect code");
      return;
    }
    setAdminTab("menu");
    const t = tables.find((x) => x.id === activeId) ?? tables[0] ?? null;
    setActiveId(t?.id ?? "t1");
    setDraftTable(t ? JSON.parse(JSON.stringify(t)) : null);
    setDirty(false);
    setModalPos({ x: 0, y: 0 });
    setAdminOpen(true);
  }

  function closeAdmin() {
    if (dirty && !window.confirm("You forgot to save. Close anyway?")) return;
    setAdminOpen(false);
    setDraftTable(null);
  }

  function switchTable(id) {
    if (dirty && !window.confirm("You forgot to save. Switch tables anyway?")) return;
    const t = tables.find((x) => x.id === id) ?? null;
    setActiveId(id);
    setDraftTable(t ? JSON.parse(JSON.stringify(t)) : null);
    setDirty(false);
  }

  function setDraftCell(rowId, colKey, value) {
    setDraftTable((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rows: prev.rows.map((r) => (r.id === rowId ? { ...r, [colKey]: value } : r)),
      };
    });
    setDirty(true);
  }

  function addRow() {
    setDraftTable((prev) => {
      if (!prev) return prev;
      const side = prev.side;
      const max = sideMax(side);
      const other = otherCountForSide(side, prev.id);
      const proposedTotal = other + (prev.rows.length + 1);
      if (proposedTotal > max) {
        setWarning(`Capped to ${max}`);
        return prev;
      }
      const blank = { id: uid() };
      prev.columns.forEach((c) => (blank[c.key] = ""));
      return { ...prev, rows: [...prev.rows, blank] };
    });
    setDirty(true);
  }

  function removeRow(rowId) {
    setDraftTable((prev) =>
      prev ? { ...prev, rows: prev.rows.filter((r) => r.id !== rowId) } : prev
    );
    setDirty(true);
  }

  function updateDraftTitle(value) {
    setDraftTable((prev) => (prev ? { ...prev, title: value } : prev));
    setDirty(true);
  }

  function updateDraftColumnLabel(colKey, value) {
    setDraftTable((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        columns: prev.columns.map((c) => (c.key === colKey ? { ...c, label: value } : c)),
      };
    });
    setDirty(true);
  }

  async function save() {
    if (!draftTable) return;
    const side = draftTable.side;
    const max = sideMax(side);
    const other = otherCountForSide(side, draftTable.id);
    const proposedTotal = other + draftTable.rows.length;
    if (proposedTotal > max) {
      setWarning(`Capped to ${max}`);
      return;
    }

    const nextTables = tables.map((t) => (t.id === draftTable.id ? draftTable : t));
    setTables(nextTables);

    // UPDATED: bgImage/logoImage now persist (Storage URLs)
    const payload = {
      tables: nextTables,
      leftCap,
      rightCap,
      logoPos,
      gridPos,
      decorImages,
      menuUserScale,
      fontScale,
      bgImage,
      logoImage,
      tableBorderRadius,
    };

    try {
      await saveConfig(payload);
    } catch (e) {
      console.warn(e);
      setWarning("Saved, but failed to sync");
    }

    setAdminOpen(false);
    setDraftTable(null);
    setDirty(false);
  }

  useEffect(() => {
    if (!warning) return;
    const t = setTimeout(() => setWarning(null), 2500);
    return () => clearTimeout(t);
  }, [warning]);

  const modalLeftUsed = useMemo(() => {
    if (!draftTable) return leftUsed;
    if (draftTable.side !== "left") return leftUsed;
    const other = otherCountForSide("left", draftTable.id);
    return other + draftTable.rows.length;
  }, [draftTable, leftUsed, tables]);

  const modalRightUsed = useMemo(() => {
    if (!draftTable) return rightUsed;
    if (draftTable.side !== "right") return rightUsed;
    const other = otherCountForSide("right", draftTable.id);
    return other + draftTable.rows.length;
  }, [draftTable, rightUsed, tables]);

  const canAddRow = useMemo(() => {
    if (!draftTable) return false;
    const side = draftTable.side;
    const max = sideMax(side);
    const other = otherCountForSide(side, draftTable.id);
    return other + (draftTable.rows.length + 1) <= max;
  }, [draftTable, leftCap, rightCap, tables]);

  const modalCapForActive = useMemo(() => {
    if (!draftTable) return 0;
    return sideMax(draftTable.side);
  }, [draftTable, leftCap, rightCap]);

  return (
    <div
      className="pageRoot"
      style={
        bgImage
          ? {
              backgroundImage: `url(${bgImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              paddingBottom: window.innerWidth < 768 ? '100px' : '0',
            }
          : { background: "linear-gradient(135deg,#1a1a1a,#101010)", paddingBottom: window.innerWidth < 768 ? '100px' : '0' }
      }
    >
      <div className="pageOverlay" />

      <div className="pageInner">
        <div className="titleWrap">
          <div
            className="logoWrap"
            style={{
              left: `${logoPos.leftPct}%`,
              top: `calc(${logoPos.top / 10} * var(--vmin))`,
              transform: "translateX(-50%)",
            }}
          >
            {logoImage ? (
              <img
                src={logoImage}
                alt="logo"
                style={{
                  height: `clamp(40px, calc(${logoPos.size / 10} * var(--vmin)), 140px)`,
                  width: "auto",
                  filter: "drop-shadow(0 18px 30px rgba(0,0,0,0.35))",
                }}
              />
            ) : null}
          </div>

          {/* Decor images (A, B, C...) */}
          {decorImages.map((d) => (
            <div
              key={d.id}
              className="decorGeneric"
              style={{
                ...(d.anchor === "right"
                  ? { right: `calc(${d.x / 10} * var(--vmin))` }
                  : { left: `calc(${d.x / 10} * var(--vmin))` }),
                top: `calc(${d.top / 10} * var(--vmin))`,
                width: `clamp(48px, calc(${d.size / 10} * var(--vmin)), 180px)`,
                zIndex: d.zIndex || 0,
              }}
            >
              {d.src ? <img src={d.src} alt={d.label} className="imgFull" style={{ transform: `rotate(${d.rotation || 0}deg)` }} /> : null}
            </div>
          ))}
        </div>

        <div
          ref={gridRef}
          className="menuGrid"
          style={{
            gap: "var(--gap)",
            left: `${gridPos.left}%`,
            top: `${gridPos.top}vh`,
            transform:
              "translate(-50%, -50%) scale(var(--menuUserScale, 1))",
            width: "90vw",
            paddingTop: window.innerWidth < 768 ? '60px' : '0',
          }}
        >
          {/* LEFT COLUMN */}
          <div className="colWrap">
            <div className="menuSideHeader">Flower</div>
            <div className="colStack">
              {tables
                .filter((t) => t.side === "left")
                .map((t) => (
                  <FrostTable key={t.id} table={t} borderRadius={tableBorderRadius} />
                ))}
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="colWrap">
            <div className="menuSideHeader" style={{ marginTop: window.innerWidth < 768 ? '20px' : '0' }}>Hash</div>
            <div className="colStack">
              {tables
                .filter((t) => t.side === "right")
                .map((t) => (
                  <div key={t.id} className="rightGroup">
                    {t.title === "Pre Rolls" ? <div className="preRollsTitle">Pre Rolls</div> : null}
                    <FrostTable table={t} borderRadius={tableBorderRadius} />
                  </div>
                ))}
            </div>
          </div>
        </div>

        <button
          onClick={openAdmin}
          className="editBtn"
          style={window.innerWidth < 768 ? { left: '50%', bottom: '20px', transform: 'translateX(-50%)' } : { right: "calc(2 * var(--vmin) + 16px)", bottom: "calc(2 * var(--vmin) + 16px)" }}
          type="button"
        >
          Edit menu
        </button>
      </div>

      {warning ? <div className="toastWarn">{warning}</div> : null}

      <Modal
        open={adminOpen}
        title="Edit menu"
        onClose={closeAdmin}
        modalPos={modalPos}
        setModalPos={setModalPos}
        footer={
          <div className="modalFooterRow">
            <div className="mutedTiny">
              Left: {modalLeftUsed}/{leftCap} | Right: {modalRightUsed}/{rightCap}
            </div>
            <div className="modalFooterBtns">
              <button onClick={closeAdmin} className="btnGhost" type="button">
                Cancel
              </button>
              <button onClick={save} className="btnLime" type="button">
                Save
              </button>
            </div>
          </div>
        }
      >
        <div className="tabRow">
          <button
            onClick={() => setAdminTab("menu")}
            className={`tabBtn ${adminTab === "menu" ? "tabActive" : "tabIdle"}`}
            type="button"
          >
            Menu
          </button>
          <button
            onClick={() => setAdminTab("design")}
            className={`tabBtn ${adminTab === "design" ? "tabActive" : "tabIdle"}`}
            type="button"
          >
            Design
          </button>
        </div>

        {adminTab === "design" ? (
          <div className="designGrid">
            <div className="designLeft">
              <div className="uploadGrid">
                <UploadControl
                  label="Background image"
                  value={bgImage}
                  onChange={async (f) => {
                    if (!f) return;
                    await deleteOldImage(bgImage);
                    const url = await uploadImage(f, "bg");
                    setBgImage(url);
                    setDirty(true);
                    // Auto-save design changes
                    await saveConfig({
                      tables,
                      leftCap,
                      rightCap,
                      logoPos,
                      gridPos,
                      decorImages,
                      menuUserScale,
                      fontScale,
                      bgImage: url,
                      logoImage,
                      tableBorderRadius,
                    });
                  }}
                />
                <UploadControl
                  label="Logo"
                  value={logoImage}
                  onChange={async (f) => {
                    if (!f) return;
                    await deleteOldImage(logoImage);
                    const url = await uploadImage(f, "logo");
                    setLogoImage(url);
                    setDirty(true);
                    // Auto-save design changes
                    await saveConfig({
                      tables,
                      leftCap,
                      rightCap,
                      logoPos,
                      gridPos,
                      decorImages,
                      menuUserScale,
                      fontScale,
                      bgImage,
                      logoImage: url,
                      tableBorderRadius,
                    });
                  }}
                />

                {/* Upload controls for all decor images */}
                {decorImages.map((d, idx) => (
                  <UploadControl
                    key={d.id}
                    label={d.label}
                    value={d.src}
                    onChange={async (f) => {
                      if (!f) return;
                      await deleteOldImage(d.src);
                      const url = await uploadImage(f, "decor");
                      const newDecor = decorImages.map((x, i) => (i === idx ? { ...x, src: url } : x));
                      setDecorImages(newDecor);
                      setDirty(true);
                      // Auto-save design changes
                      await saveConfig({
                        tables,
                        leftCap,
                        rightCap,
                        logoPos,
                        gridPos,
                        decorImages: newDecor,
                        menuUserScale,
                        fontScale,
                        bgImage,
                        logoImage,
                        tableBorderRadius,
                      });
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="panelCard">
              <div>
                <div className="panelTitle">Placement</div>

                <div className="panelCardInner" style={{ marginBottom: '10px' }}>
                  <div className="mutedTiny">Number of decor images</div>
                  <input
                    type="number"
                    min={0}
                    max={12}
                    value={decorCount}
                    onChange={(e) => {
                      const next = Math.max(0, Math.min(12, Number(e.target.value || 0)));
                      setDecorCount(next);

                      setDecorImages((prev) => {
                        const out = prev.slice(0, next);
                        for (let i = out.length; i < next; i++) out.push(makeDecor(i));
                        // keep labels consistent A,B,C...
                        return out.map((d, idx) => ({
                          ...d,
                          label: `Image ${String.fromCharCode(65 + idx)}`,
                        }));
                      });

                      setDirty(true);
                    }}
                    className="numInput"
                    style={{ width: '60px' }}
                  />
                </div>

                {/* Placement editor for each decor image */}
                <div className="placementGrid">
                  {decorImages.map((d, idx) => (
                    <div key={d.id} className="panelCardInner">
                      <div className="mutedTiny" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>{d.label} <button onClick={() => removeDecor(idx)} style={{fontSize:'calc(10px * var(--fontScale))', padding:'2px 4px'}}>X</button></div>

                      <div className="doubleGrid">
                        <div>
                          <div className="label11">Anchor</div>
                          <select
                            value={d.anchor}
                            onChange={(e) => {
                              const anchor = e.target.value;
                              setDecorImages((prev) =>
                                prev.map((x, i) => (i === idx ? { ...x, anchor } : x))
                              );
                              setDirty(true);
                            }}
                            className="numInput"
                          >
                            <option value="left">Left</option>
                            <option value="right">Right</option>
                          </select>
                        </div>

                        <div>
                          <div className="label11">{d.anchor === "right" ? "Right" : "Left"}</div>
                          <input
                            type="number"
                            value={d.x}
                            onChange={(e) => {
                              const x = Number(e.target.value || 0);
                              setDecorImages((prev) => prev.map((v, i) => (i === idx ? { ...v, x } : v)));
                              setDirty(true);
                            }}
                            className="numInput"
                          />
                        </div>
                      </div>

                      <div className="doubleGrid">
                        <div>
                          <div className="label11">Top</div>
                          <input
                            type="number"
                            value={d.top}
                            onChange={(e) => {
                              const top = Number(e.target.value || 0);
                              setDecorImages((prev) => prev.map((v, i) => (i === idx ? { ...v, top } : v)));
                              setDirty(true);
                            }}
                            className="numInput"
                          />
                        </div>

                        <div>
                          <div className="label11">Size</div>
                          <input
                            type="number"
                            value={d.size}
                            onChange={(e) => {
                              const size = Number(e.target.value || 0);
                              setDecorImages((prev) => prev.map((v, i) => (i === idx ? { ...v, size } : v)));
                              setDirty(true);
                            }}
                            className="numInput"
                          />
                        </div>
                      </div>

                      <div className="doubleGrid">
                        <div>
                          <div className="label11">Z-Index</div>
                          <select
                            value={d.zIndex || 0}
                            onChange={(e) => {
                              const zIndex = Number(e.target.value);
                              setDecorImages((prev) => prev.map((v, i) => (i === idx ? { ...v, zIndex } : v)));
                              setDirty(true);
                            }}
                            className="numInput"
                          >
                            <option value={0}>Default</option>
                            <option value={10}>Front</option>
                          </select>
                        </div>

                        <div>
                          <div className="label11">Rotation</div>
                          <input
                            type="number"
                            min="0"
                            max="360"
                            value={d.rotation || 0}
                            onChange={(e) => {
                              const rotation = Number(e.target.value);
                              setDecorImages((prev) => prev.map((v, i) => (i === idx ? { ...v, rotation } : v)));
                              setDirty(true);
                            }}
                            className="numInput"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="panelCardInner mt12">
                  <div className="mutedTiny">Table Border Radius</div>
                  <select
                    value={tableBorderRadius}
                    onChange={(e) => {
                      setTableBorderRadius(e.target.value);
                      setDirty(true);
                    }}
                    className="numInput"
                  >
                    <option value="0px">Sharp</option>
                    <option value="4px">Slightly Rounded</option>
                    <option value="8px">Normal Rounded</option>
                    <option value="16px">Extra Rounded</option>
                  </select>
                </div>

                <div className="panelCardInner mt12">
                  <div className="mutedTiny">Logo</div>
                  <div className="tripleGrid">
                    <div>
                      <div className="label11">Left %</div>
                      <input
                        type="number"
                        value={logoPos.leftPct}
                        min={0}
                        max={100}
                        onChange={(e) => {
                          const v = Number(e.target.value || 0);
                          setLogoPos((p) => ({ ...p, leftPct: Math.max(0, Math.min(100, v)) }));
                          setDirty(true);
                        }}
                        className="numInput"
                      />
                    </div>
                    <div>
                      <div className="label11">Top</div>
                      <input
                        type="number"
                        value={logoPos.top}
                        onChange={(e) => {
                          setLogoPos((p) => ({ ...p, top: Number(e.target.value || 0) }));
                          setDirty(true);
                        }}
                        className="numInput"
                      />
                    </div>
                    <div>
                      <div className="label11">Size</div>
                      <input
                        type="number"
                        value={logoPos.size}
                        min={30}
                        max={200}
                        onChange={(e) => {
                          setLogoPos((p) => ({ ...p, size: Math.max(30, Number(e.target.value || 30)) }));
                          setDirty(true);
                        }}
                        className="numInput"
                      />
                    </div>
                  </div>
                </div>

                <div className="panelCardInner mt12">
                  <div className="mutedTiny">Menu grid</div>
                  <div className="doubleGrid">
                    <div>
                      <div className="label11">Left (%)</div>
                      <input
                        type="number"
                        value={gridPos.left}
                        onChange={(e) => {
                          setGridPos((p) => ({ ...p, left: Number(e.target.value || 0) }));
                          setDirty(true);
                        }}
                        className="numInput"
                      />
                    </div>
                    <div>
                      <div className="label11">Top (vh)</div>
                      <input
                        type="number"
                        value={gridPos.top}
                        onChange={(e) => {
                          setGridPos((p) => ({ ...p, top: Number(e.target.value || 0) }));
                          setDirty(true);
                        }}
                        className="numInput"
                      />
                    </div>
                  </div>

                  {/* NEW: grid scale control */}
                  <div className="mt12">
                    <div className="label11">Scale</div>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="3"
                      value={menuUserScale}
                      onChange={(e) => {
                        const v = Number.parseFloat(e.target.value || "1");
                        const next = Math.max(0, Math.min(3, Number.isFinite(v) ? v : 1));
                        setMenuUserScale(next);
                        setDirty(true);
                      }}
                      className="numInput"
                    />
                  </div>

                  <div className="mt12">
                    <div className="label11">Font size (%)</div>
                    <input
                      type="number"
                      min="50"
                      max="200"
                      step="5"
                      value={Math.round(fontScale * 100)}
                      onChange={(e) => {
                        const pct = Number(e.target.value || 100);
                        const clamped = Math.max(50, Math.min(200, Number.isFinite(pct) ? pct : 100));
                        setFontScale(clamped / 100);
                        setDirty(true);
                      }}
                      className="numInput"
                    />
                    <div className="mutedTiny">100% = original text size</div>
                  </div>
                </div>
              </div>

              <div>
                <div className="rowCapsHeader">
                  <div className="panelTitle">Row caps</div>
                  <label className="capsUnlock">
                    <input
                      type="checkbox"
                      checked={capsUnlocked}
                      onChange={(e) => setCapsUnlocked(e.target.checked)}
                    />
                    Unlock
                  </label>
                </div>

                <div className="capsStack">
                  <div className="capsRow">
                    <div className="mutedTiny">Left cap</div>
                    <div className="mutedTiny">Used {modalLeftUsed}</div>
                  </div>
                  <input
                    type="number"
                    value={leftCap}
                    min={1}
                    max={80}
                    disabled={!capsUnlocked}
                    onChange={(e) => {
                      setLeftCap(Math.max(1, Number(e.target.value || 1)));
                      setDirty(true);
                    }}
                    className="numInput wide"
                  />

                  <div className="capsRow">
                    <div className="mutedTiny">Right cap</div>
                    <div className="mutedTiny">Used {modalRightUsed}</div>
                  </div>
                  <input
                    type="number"
                    value={rightCap}
                    min={1}
                    max={80}
                    disabled={!capsUnlocked}
                    onChange={(e) => {
                      setRightCap(Math.max(1, Number(e.target.value || 1)));
                      setDirty(true);
                    }}
                    className="numInput wide"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {adminTab === "menu" ? (
          <div className="menuTabWrap">
            <div className="tablePicker">
              {tables.map((t) => (
                <button
                  key={t.id}
                  onClick={() => switchTable(t.id)}
                  className={`pillBtn ${activeId === t.id ? "pillActive" : "pillIdle"}`}
                  type="button"
                >
                  {t.title}
                </button>
              ))}
            </div>

            {draftTable ? (
              <div className="menuEditor">
                <div className="panelCard">
                  <div className="mutedTiny mb8">Category name</div>
                  <TextInput value={draftTable.title} onChange={updateDraftTitle} placeholder="Category title" />
                </div>

                <div className="panelCard">
                  <div className="mutedTiny mb8">Headings</div>
                  <div className="hScroll">
                    <div className="headingsRow">
                      {draftTable.columns
                        .filter((c) => c.key !== "name")
                        .map((c) => (
                          <input
                            key={c.key}
                            value={c.label}
                            onChange={(e) => updateDraftColumnLabel(c.key, e.target.value)}
                            placeholder="1G"
                            className="headingInput"
                          />
                        ))}
                    </div>
                  </div>
                </div>

                <div className="tableShell">
                  <div className="hScroll">
                    <table className="adminTable">
                      <thead className="adminThead">
                        <tr>
                          {draftTable.columns.map((c) => (
                            <th key={c.key} className="adminTh">
                              {c.key === "name" ? "Name" : c.label || c.key}
                            </th>
                          ))}
                          <th className="adminTh">Remove</th>
                        </tr>
                      </thead>

                      <tbody>
                        {draftTable.rows.map((row) => (
                          <tr key={row.id} className="adminTr">
                            {draftTable.columns.map((c) => (
                              <td key={c.key} className="adminTd">
                                <TextInput
                                  value={row[c.key] ?? ""}
                                  onChange={(v) => setDraftCell(row.id, c.key, v)}
                                  placeholder={c.key === "name" ? "Item" : "Value"}
                                  className={c.key === "name" ? "minName" : "minValue"}
                                />
                              </td>
                            ))}
                            <td className="adminTd">
                              <button onClick={() => removeRow(row.id)} className="btnGhostSmall" type="button">
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}

                        {draftTable.rows.length === 0 ? (
                          <tr>
                            <td colSpan={draftTable.columns.length + 1} className="emptyAdmin">
                              No rows yet. Click Add row.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="addRowBar">
                  <div className="mutedTiny">Cap for this side: {modalCapForActive}</div>
                  <button
                    onClick={() => {
                      if (!canAddRow) {
                        setWarning(`Capped to ${modalCapForActive}`);
                        return;
                      }
                      addRow();
                    }}
                    className="btnWhite"
                    type="button"
                  >
                    Add row
                  </button>
                </div>
              </div>
            ) : (
              <div className="muted">Select a table to edit.</div>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function App() {
  return (
    <div className="appRoot">
      <MenuPage />
    </div>
  );
}

export default App;
